// A Material UI feature.
// This sets the default theme. When imported, and passed to a ThemeProvider, components can access this theme.
// This is different from the root styles.css file in that this is responsible only for MUI components.

// TODO: UI Upgrade - This will need to be upgraded to import from @mui/material/styles
// This should also probably be moved to the src folder instead of portico folder.
import { createTheme } from "@material-ui/core/styles";

const defaultTheme = createTheme();

/**
 * Move colors / margins / paddings to variables at index.css
 */
const CustomTheme = createTheme({
  palette: {
    primary: {
      main: "#007DB8",
    },
    secondary: {
      main: "#fff",
    },
    text: {
      primary: "#3A3A3A",
      secondary: "var(--dark-grey)",
    },
    background: {
      default: "#F5F5F5"
    }
  },
  typography: {
    fontFamily: "var(--font-family-base)",
    h1: {
      fontSize: "var(--h1-text-size)",
      fontWeight: 500,
      fontFamily: "var(--font-family-bold)",
    },
    h2: {
      fontSize: "var(--h2-text-size)",
      fontFamily: "var(--font-family-bold)",
    },
    body1: {
      fontSize: "var(--default-text-size)",
    },
    body2: {
      fontSize: "var(--default-text-bold-size)",
      fontFamily: "var(--font-family-bold)",
    },
    subtitle1: {
      fontFamily: "var(--font-family-bold)",
      color: "var(--deep-sea-blue)",
    },
    subtitle2: {
      fontSize: "var(--subtitle2-text-size)",
      fontWeight: "normal",
    },
    h3: {
      fontSize: "var(--h2-text-size)",
    },
    h4: {
      fontFamily: "var(--font-family-bold)",
      fontSize: "var(--h4-text-size)",
      lineHeight: "var(--h4-text-line-height)",
    },
  },
  mixins: {
    toolbar: {
      height: "79px",
    },
  },
  overrides: {
    /**
     * Styles for base input override
     */
    MuiTextField: {
      root: {  
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
          {
            border: "1px solid var(--teal-blue)",
          },
          '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline':{
            borderColor:'var(--light-venetian-red) !important'
          },
          "& .MuiOutlinedInput-notchedOutline legend":{
            maxWidth:"0px"
          },
          background:'#FFF'
      },
    },
    MuiInputBase:{
      root:{
        height: "var(--input-height)",
        fontSize: "var(--font-size-input)",
        color: "var(--neutrals-black)",
      }
    },
    MuiOutlinedInput:{
      root:{
        '&.MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]':{
          padding:'0px 9px'
        },
        notchedOutline:{
          borderColor:"var(--neutrals-medium-grey)"
        }
      }
    },
    MuiInput: {
      root: {
        border: "1px solid var(--neutrals-medium-grey)",
        borderRadius: "4px",
        backgroundColor: "var(--white)",
        "&$focused": {
          border: "1px solid var(--teal-blue)",
        },
        "&$error": {
          border: "1px solid var(--input-error-border-color)",
        },
        "&:hover": {
          border: "1px solid var(--teal-blue)",
        },
        "&$error:hover": {
          border: "1px solid var(--input-error-border-color)",
        },
        "&$disabled": {
          border: "1px solid var(--input-disabled-border-color)",
        },
      },
      input: {
        padding: `0 ${defaultTheme.spacing(1)}px`,
        height: "var(--input-height)",
        borderRadius: "4px",
      },
      underline: {
        "&&:before": {
          borderBottom: "0px",
        },
        "&&:after": {
          borderBottom: "0px",
        },
        "&&&&:hover:before": {
          borderBottom: "0px",
        },
      },
      formControl: {
        "label + &": {
          marginTop: 0,
        },
      },
    },
    MuiChip:{
      root:{
        height:'30px'
      }
    },
    MuiContainer: {
      root: {
        paddingTop: "30px",
      },
    },
    MuiSelect: {
      select: {
        "&:focus": {
          backgroundColor: "rgba(0, 0, 0, 0)",
        },
      },
      selectMenu: {
        lineHeight: "2.4",
        minHeight: "35px",
      },
    },
    MuiButton: {
      root: {
        textTransform: "none",
      },
    },

    /**
     * Styles for base label override
     */
    MuiInputLabel: {
      outlined: {
        transform: "translate(14px, 15px) scale(1)",
        padding: "0px 5px !important",
        background: "#FFF",
        marginLeft: "-9px",
      },
      root: {
        fontSize: "var(--input-label-font-size)",
        color: "var(--light-grey)",
        "&$focused": {
          color: "var(--light-grey)",
        },
      },
      formControl: {
        transform: "none",
      },
      shrink: {
        transform: "none",
      },
      animated: {
        transition: "none",
      },
    },

    MuiTabs: {
      indicator: {
        backgroundColor: "var(--primary-light)",
      },
    },

    MuiTab: {
      root: {
        textTransform: "capitalize",
        fontSize: "13px",
        color: "var(--neutrals-grey)",
        "&$selected": {
          color: "var(--primary-light)",
        },
      },
    },

    MuiCheckbox: {
      colorPrimary: {
        "&$checked": {
          color: "var(--teal-blue) !important",
        },
      },
    },
    
    MuiPaper:{
      root:{
        '& .Mui-selected, & .Mui-selected:hover, & .Mui-selected:focus':{
          background:'var(--teal-blue) !important'
        }
      }
    },

    MuiStepIcon: {
      root: {
        color: "var(--neutrals-grey)",
        "&$active": {
          color: "var(--teal-blue)",
        },
        "&$completed": {
          color: "var(--neutrals-green)",
        },

        "& $text": {
          fill: "var(--white)",
        },
      },
    },
    MuiStepConnector: {
      root: {
        "&$vertical": {
          marginTop: "6px",
          marginBottom: "6px",
          padding: "0px",
        },

        "& $lineVertical": {
          minHeight: "32px",
        },
      },
    },
    MuiStepLabel: {
      root: {
        alignItems: "start",
        "& $labelContainer": {
          marginTop: "3px",
          marginBottom: "-25px",

          "& $label": {
            color: "var(--neutrals-grey)",
            fontWeight: "normal",
            fontSize: "14px",

            "& p": {
              color: "var(--neutrals-grey)",
              fontSize: "12px",
              lineHeight: "16px",
              marginTop: "6px",
              position: "absolute",
            },

            "& > div": {
              paddingLeft: "0px",
              borderLeft: "1px solid #bdbdbd",
              marginLeft: "-20px",
              marginTop: "8px",
              marginBottom: "-8px",

              "& a": {
                fontSize: "13px",
                textDecoration: "none",
                cursor: "pointer",
                color: "var(--neutrals-grey)",
              },
            },

            "&$completed": {
              color: "var(--neutrals-black)",
            },
            "&$active": {
              color: "var(--neutrals-black)",
              fontWeight: 500,

              "& p": {
                color: "var(--neutrals-black)",
              },
            },
          },
        },
      },
    },
  },
});

export default CustomTheme;